/// <amd-module name="Repos/Discussion/Components/PreviewComment" />
import { MarkdownPreviewWithAutocomplete } from "Mention/MarkdownWithAutocomplete/Components";
import * as React from "react";
import { DiscussionStatus, IDiscussionCommentModel } from "Repos/Discussion/Common/DiscussionContracts";
import * as Resources from "Repos/Discussion/Resources";
import { IReadonlyObservableValue } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { localeFormat } from "VSS/Core/Util/String";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Ago } from "VSSUI/Ago";
import { Button } from "VSSUI/Button";
import { Dropdown, DropdownExpandableButton } from "VSSUI/Dropdown";
import { FocusZone, FocusZoneDirection } from "VSSUI/FocusZone";
import { ListSelection } from "VSSUI/List";
import { IMenuItem, MoreButton } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { Spinner } from "VSSUI/Spinner";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { Location } from "VSSUI/Utilities/Position";
import { ScreenSizeConditional, ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { DiscussionThreadPermissions } from "../Components/DiscussionThread.Props";
import { discussionStatusListBoxItems } from "../Utils/DiscussionStatus";
import { IPreviewCommentProps } from "./PreviewComment.Props";
import { UpdateComment } from "./UpdateComment";
import { copyToClipboard } from "VSSUI/Utils/ClipboardUtils";

export const PreviewComment = (props: IPreviewCommentProps): JSX.Element => {
    const {
        author,
        comment,
        commentEditorClassName,
        first,
        focusEditButton,
        hasReplies,
        isShim,
        markdownEditorProps = {},
        onChangeStatus,
        onUpdateComment,
        permissions,
        personaProvider,
        status,
        baseUrl
    } = props;
    const [autoFocusEditButton, setAutoFocusEditButton] = React.useState(focusEditButton);
    const canChangeStatus = (permissions & DiscussionThreadPermissions.ChangeStatus) > 0;

    const onUpdate = (commentId: number, comment: string, status?: DiscussionStatus): void => {
        onUpdateComment && onUpdateComment(commentId, comment, status);
        setAutoFocusEditButton(true);
    };

    const commentPublishedDate = toTimestamp(comment.getPublishedDate()?.toString() ?? '');
    var commentIdDiv = commentPublishedDate.toString();
    const highlighted = location.hash === "#" + commentIdDiv && "highlighted";

    var elem = document.getElementById(commentIdDiv);
   
    if (elem && elem.classList.contains("highlighted")) {
        elem.scrollIntoView({
            block: "center",
            behavior: "smooth",
            inline: "nearest"
        });
    }
   
    if (!isShim) {
        return (
            <div id={commentIdDiv} className={css("comment-viewer", commentEditorClassName, highlighted)}>
              
                <div className={css("repos-discussion-comment flex-column true", first && hasReplies && "has-replies")}>
                <Observer isDeleted={comment.isDeleted()} isEditing={comment.isEditing()}>
                    {(statusProps: { isDeleted: boolean; isEditing: boolean }) =>
                        statusProps.isDeleted ? (
                            <div className="flex-row flex-center">
                                <div className="repos-discussion-deleted-comment-circle" />
                                <div className="repos-discussion-deleted-comment flex-grow padding-vertical-4 padding-left-8">
                                    {Resources.CommentDeletedMessage}
                                </div>
                                {first && (
                                    <ChangeStatus className="margin-4" disabled={!canChangeStatus} onChangeStatus={onChangeStatus} status={status} />
                                )}
                            </div>
                        ) : statusProps.isEditing ? (
                            <UpdateComment
                                author={author}
                                className={commentEditorClassName}
                                comment={comment}
                                markdownEditorProps={markdownEditorProps}
                                onUpdateComment={onUpdate}
                                personaProvider={personaProvider}
                                status={status}
                                onCancel={() => setAutoFocusEditButton(true)}
                            />
                        ) : (
                            <div className="flex-column">
                                <ScreenSizeConditional condition={size => size === ScreenSize.xsmall}>
                                    {first && (
                                        <ChangeStatus
                                            className="margin-4 margin-bottom-16"
                                            disabled={!canChangeStatus}
                                            onChangeStatus={onChangeStatus}
                                            status={status}
                                        />
                                    )}
                                </ScreenSizeConditional>
                                <div className="flex-row">
                                    <Persona
                                        className="repos-comment-header-persona"
                                        identity={comment.getAuthor()}
                                        personaProvider={personaProvider}
                                        size={PersonaSize.size24}
                                    />
                                    <div className="repos-comment-editor-fit flex-column flex-grow scroll-hidden padding-left-8">
                                        <PreviewCommentHeader {...props} focusEditButton={autoFocusEditButton} />
                                        <MarkdownPreviewWithAutocomplete
                                            {...markdownEditorProps.previewProps}
                                            className="repos-comment-editor-max-width"
                                            value={comment.getContent().value}
                                        />
                                    </div>
                                </div>
                            </div>
                        )
                    }
                </Observer>
                </div>
            </div>
        );
    }
  
    return (
        <div id={commentIdDiv} className={css("comment-viewer", commentEditorClassName, highlighted)}>
        <div className={css("repos-discussion-comment flex-column", first && hasReplies && "has-replies")}>
            <Spinner className={"padding-16"} />
            </div>
        </div>
    );
};

const PreviewCommentHeader = (props: IPreviewCommentProps): JSX.Element => {
    const { author, comment, first, onChangeStatus, onLikeComment, onRemoveComment, permissions, showLikes, status, focusEditButton, baseUrl } = props;

    const lastContentUpdatedDate = comment.getLastContentUpdatedDate();

    const canManageComment = comment.isOwner(author) && (permissions & DiscussionThreadPermissions.ManageComment) > 0;
    const canLikeComment = showLikes && (permissions & DiscussionThreadPermissions.LikeComment) > 0;
    const canChangeStatus = (permissions & DiscussionThreadPermissions.ChangeStatus) > 0;
    return (
        <ScreenSizeObserver>
            {(screenSizeProps: { screenSize: ScreenSize }) => {
                const xsmall = screenSizeProps.screenSize === ScreenSize.xsmall;
                const large = screenSizeProps.screenSize >= ScreenSize.large;

                return (
                    <div className="repos-discussion-comment-header flex-row flex-grow flex-center rhythm-horizontal-4 margin-bottom-8">
                        <div className={css("scroll-hidden", large ? "flex-row flex-center" : "flex-column")}>
                            <Tooltip overflowOnly={true}>
                                <span className="font-weight-semibold padding-right-8 text-ellipsis">{comment.getAuthor().displayName}</span>
                            </Tooltip>
                            {lastContentUpdatedDate && (
                                <div className={css("flex-row flex-center rhythm-horizontal-4", large && "margin-left-4")}>
                                    <Observer isUnread={comment.isUnread()}>
                                        {(unreadProps: { isUnread: boolean }) =>
                                            unreadProps.isUnread ? <div className="repos-unread-comment-marker flex-noshrink" /> : null
                                        }
                                    </Observer>
                                    <Ago className="body-s secondary-text margin-right-4" date={lastContentUpdatedDate} format={AgoFormat.Compact} />
                                </div>
                            )}
                        </div>
                        <FocusZone direction={FocusZoneDirection.Horizontal}>
                            <div className={css("flex-row flex-grow flex-noshrink justify-end", xsmall ? "flex-start" : "flex-center")}>
                                <Observer likes={comment.getLikes()}>
                                    {(props: { likes: IdentityRef[] }) => {
                                        const hasLikes = props.likes.length > 0;
                                        const commentLiked = comment.liked(author);
                                        const moreButtonItems: IMenuItem[] = [];
                                        if (xsmall) {
                                            if (canLikeComment && !hasLikes) {
                                                moreButtonItems.push({
                                                    id: "like-comment-menu-item",
                                                    iconProps: { iconName: "Like" },
                                                    onActivate: () => onLikeComment && onLikeComment(comment.getId(), !commentLiked, author),
                                                    text: Resources.LikeTooltipText
                                                });
                                            }
                                            if (canManageComment) {
                                                moreButtonItems.push({
                                                    id: "edit-comment-menu-item",
                                                    iconProps: { iconName: "Edit" },
                                                    onActivate: () => comment.startEditing(),
                                                    text: Resources.EditTooltipText
                                                });
                                                moreButtonItems.push({
                                                    id: "delete-comment-menu-item",
                                                    iconProps: { iconName: "Delete" },
                                                    onActivate: () => onRemoveComment && onRemoveComment(comment.getId()),
                                                    text: Resources.DeleteTooltipText
                                                });
                                            }
                                        }
                                        return (
                                            <>
                                                {canManageComment && !xsmall && (
                                                    <>
                                                        <Button
                                                            ariaLabel={Resources.EditTooltipText}
                                                            className="repos-discussion-comment-auto-hide-button"
                                                            iconProps={{ className: "repos-discussion-comment-auto-hide", iconName: "Edit" }}
                                                            onClick={() => comment.startEditing()}
                                                            subtle={true}
                                                            tabIndex={0}
                                                            tooltipProps={{ text: Resources.EditTooltipText }}
                                                            autoFocus={focusEditButton}
                                                        />
                                                        <Button
                                                            ariaLabel={Resources.DeleteTooltipText}
                                                            className="repos-discussion-comment-auto-hide-button"
                                                            iconProps={{ className: "repos-discussion-comment-auto-hide", iconName: "Delete" }}
                                                            onClick={() => onRemoveComment && onRemoveComment(comment.getId())}
                                                            subtle={true}
                                                            tabIndex={0}
                                                            tooltipProps={{ text: Resources.DeleteTooltipText }}
                                                        />
                                                    </>
                                                )}
                                                {canLikeComment && (!xsmall || hasLikes) && (
                                                    <Button
                                                        ariaLabel={Resources.LikeTooltipText}
                                                        className={css(!hasLikes && "repos-discussion-comment-auto-hide-button")}
                                                        iconProps={{
                                                            className: css("no-padding", !hasLikes && "repos-discussion-comment-auto-hide"),
                                                            iconName: commentLiked ? "LikeSolid" : "Like"
                                                        }}
                                                        onClick={() => onLikeComment && onLikeComment(comment.getId(), !commentLiked, author)}
                                                        subtle={true}
                                                        tabIndex={0}
                                                        tooltipProps={{
                                                            renderContent: () =>
                                                                hasLikes
                                                                    ? props.likes.map((like, index) => (
                                                                          <div key={`discussion-like-${index}`}>{like.displayName}</div>
                                                                      ))
                                                                    : null,
                                                            text: !hasLikes ? Resources.LikeTooltipText : undefined
                                                        }}
                                                    >
                                                        {hasLikes && <span className="body-s margin-left-4">{props.likes.length}</span>}
                                                    </Button>
                                                )}
                                                <Button
                                                    className="comment-viewers"
                                                    iconProps={{ iconName: "Link" }}
                                                    ariaLabel={Resources.CopyCommentLink}
                                                    onClick={() => handleCopyLink(baseUrl !== undefined ? baseUrl : "", comment)}
                                                    autoFocus={focusEditButton}
                                                    tooltipProps={{ text: Resources.CopyCommentLink }}
                                                    role="menuitem"
                                                    subtle
                                                />
                                                {moreButtonItems.length > 0 && (
                                                    <MoreButton
                                                        contextualMenuProps={{
                                                            menuProps: {
                                                                id: "manage-comments-menu",
                                                                items: moreButtonItems
                                                            }
                                                        }}
                                                        iconProps={{ iconName: "MoreVertical" }}
                                                        tabIndex={0}
                                                    />
                                                )}
                                                {!xsmall && first && (
                                                    <ChangeStatus
                                                        disabled={!canChangeStatus}
                                                        onChangeStatus={onChangeStatus}
                                                        status={status}
                                                        subtle={true}
                                                        tabIndex={0}
                                                    />
                                                )}
                                            </>
                                        );
                                    }}
                                </Observer>
                            </div>
                        </FocusZone>
                    </div>
                );
            }}
        </ScreenSizeObserver>
    );
};

const toTimestamp = (strDate: string) => {
    var datum: number = Date.parse(strDate);
    return datum / 1000;
}

const handleCopyLink = (baseUrl: string, comment: IDiscussionCommentModel) => {
    const commentPublishedDate = toTimestamp(comment.getPublishedDate()?.toString() ?? '');
    if (baseUrl.includes("#")) {
        baseUrl = baseUrl.substring(0, baseUrl.indexOf('#'));
    }
    copyToClipboard(baseUrl + `#${commentPublishedDate}`);
}

interface IChangeStatusProps {
    className?: string;
    disabled?: boolean;
    excludeTabStop?: boolean;
    onChangeStatus?: (newStatus: DiscussionStatus) => void;
    status?: IReadonlyObservableValue<DiscussionStatus>;
    subtle?: boolean;
    tabIndex?: number;
}

class ChangeStatus extends VssComponent<IChangeStatusProps> {
    private selection = new ListSelection({ selectOnFocus: false });
    private ariaLabelText: string | undefined;
    constructor(props: IChangeStatusProps, context: IVssComponentContext) {
        super(props, context);

        this.refreshSelection();
        this.observe([{ propertyName: "status", observer: this.refreshSelection }]);
    }

    public render(): JSX.Element {
        const { className, disabled, excludeTabStop, onChangeStatus, status, subtle, tabIndex } = this.props;

        return (
            <Observer status={status}>
                {(statusProps: { status: DiscussionStatus }) => {
                    return (
                        <Dropdown
                            className={className}
                            disabled={disabled}
                            excludeTabStop={excludeTabStop}
                            items={discussionStatusListBoxItems}
                            onSelect={(event, item) => {
                                onChangeStatus?.(item.data as DiscussionStatus);
                            }}
                            ariaLabel={this.ariaLabelText}
                            placeholder={Resources.DiscussionStatusUnknown}
                            renderExpandable={props => (
                                <DropdownExpandableButton
                                    {...props}
                                    anchorOrigin={{ horizontal: Location.start, vertical: Location.end }}
                                    dropdownOrigin={{ horizontal: Location.start, vertical: Location.start }}
                                    subtle={subtle}
                                    tabIndex={tabIndex}
                                />
                            )}
                            selection={this.selection}
                            width={200}
                        />
                    );
                }}
            </Observer>
        );
    }

    private refreshSelection = () => {
        const { status } = this.props;
        const index = status ? discussionStatusListBoxItems.findIndex(item => item.data === status.value) : -1;
        if (index < 0) {
            this.selection.clear();
        } else {
            this.ariaLabelText = localeFormat(Resources.DiscussionStatusAriaLabel, discussionStatusListBoxItems[index].text);
            this.selection.select(index);
        }
    };
}
